import { useState, useEffect, useContext, useMemo } from "react";
import { Offcanvas } from "react-bootstrap";
import { Loader2 } from "lucide-react";
import { gql, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import StarekContext from "../../context/StarekContext";
import { subscriptionLimitMessage } from "../../utils/subscriptionErrors";
import { SaveSuccessPanel } from "../../components/Sidebar";
import {
  GET_OPERATIONS_FOR_PLANT,
  CREATE_MANY_CAPABILITY,
  operationToOption,
} from "./deviceCapabilities";

const DEVICE_BIND = gql`
  mutation DeviceBind(
    $updatedById: Float!
    $createdById: Float!
    $plantId: Float!
    $deviceId: String!
  ) {
    deviceBind(
      updated_by_id: $updatedById
      created_by_id: $createdById
      plant_id: $plantId
      device_id: $deviceId
    )
  }
`;

const DEVICE_UNBIND = gql`
  mutation DeviceUnbind($deviceId: String!) {
    deviceUnbind(device_id: $deviceId)
  }
`;

const BindDeviceSidebar = ({ isOpen, onClose, onBindingComplete }) => {
  const navigate = useNavigate();
  const starekContext = useContext(StarekContext);
  const [hardwareIds, setHardwareIds] = useState("");
  const [devices, setDevices] = useState([]); // [{ id, status: "binding"|true|false, errorMessage? }]

  const [bindOne, { loading: binding }] = useMutation(DEVICE_BIND);
  const [unbindOne, { loading: unbinding }] = useMutation(DEVICE_UNBIND);

  // Operations picked here are applied to every device in this binding batch.
  // Leaving it empty binds devices with no operations linked, which is valid.
  const [selectedOperations, setSelectedOperations] = useState([]);

  const { data: operationsData, loading: loadingOperations } = useQuery(
    GET_OPERATIONS_FOR_PLANT,
    {
      client: starekContext.engineClient,
      skip: !isOpen || !starekContext.lastUsedPlant?.id,
      variables: {
        where: {
          plant_id: { equals: starekContext.lastUsedPlant?.id },
          is_active: { equals: true },
        },
      },
    }
  );

  const operationOptions = useMemo(
    () => (operationsData?.operations || []).map(operationToOption),
    [operationsData?.operations]
  );

  useEffect(() => {
    if (!isOpen) {
      setDevices([]);
      setHardwareIds("");
      setSelectedOperations([]);
    }
  }, [isOpen]);

  const handleDeviceBinding = async (ids) => {
    // mark all as "binding"
    setDevices((prev) => {
      const setIds = new Set(prev.map((d) => d.id));
      const next = [...prev];
      ids.forEach((id) => {
        if (!setIds.has(id)) next.push({ id, status: "binding" });
        else
          next.forEach((d) => {
            if (d.id === id) d.status = "binding";
          });
      });
      return next;
    });

    const createdById = Number(starekContext?.user?.id);
    const updatedById = Number(starekContext?.user?.id);
    const plantId = Number(starekContext?.lastUsedPlant?.id);

    const boundIds = [];

    for (const id of ids) {
      try {
        await bindOne({
          variables: { updatedById, createdById, plantId, deviceId: id },
        });
        boundIds.push(id);
        setDevices((prev) =>
          prev.map((d) =>
            d.id === id
              ? {
                  ...d,
                  status: true,
                  errorMessage: undefined,
                  created_at: new Date().toISOString(),
                }
              : d
          )
        );
      } catch (err) {
        // A device refused for the plan's licence limit is not "already bound" —
        // say what the server said, so the user knows to upgrade rather than
        // hunting for a conflict that isn't there.
        const limitMessage = subscriptionLimitMessage(err);
        setDevices((prev) =>
          prev.map((d) =>
            d.id === id
              ? {
                  ...d,
                  status: false,
                  errorMessage:
                    limitMessage ||
                    err?.message ||
                    "It seems like this device is already bound or not eligible. Please check again.",
                }
              : d
          )
        );
        if (limitMessage) {
          starekContext.showError?.(limitMessage);
          // Remaining devices would be refused for the same reason.
          break;
        }
      }
    }

    // Link the picked operations to every device that bound successfully.
    // Devices that failed to bind are skipped; a failure here never fails the bind.
    if (boundIds.length && selectedOperations.length) {
      try {
        await starekContext.engineClient.mutate({
          mutation: CREATE_MANY_CAPABILITY,
          variables: {
            data: boundIds.flatMap((deviceId) =>
              selectedOperations.map((operation) => ({
                device_id: deviceId,
                operation_id: operation.value,
              }))
            ),
          },
        });
      } catch (err) {
        starekContext.showError?.(
          `Devices were bound, but linking operations failed: ${
            err?.message || err
          }`
        );
      }
    }

    starekContext.loadSubscription?.(starekContext.lastUsedWorkspace);

    if (typeof onBindingComplete === "function") {
      onBindingComplete();
    }
  };

  const handleDeviceUnbinding = async (deviceId) => {
    try {
      await unbinding;
      await unbindOne({ variables: { deviceId } });
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
      if (typeof onBindingComplete === "function") onBindingComplete();
    } catch (err) {
      setDevices((prev) =>
        prev.map((d) =>
          d.id === deviceId
            ? {
                ...d,
                status: false,
                errorMessage: err?.message || "Unbind failed",
              }
            : d
        )
      );
    }
  };

  const handleStartBinding = () => {
    if (!hardwareIds.trim()) return;
    const ids = hardwareIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // add to local list (if not present), mark as binding immediately
    setDevices((prev) => {
      const setIds = new Set(prev.map((d) => d.id));
      const add = ids
        .filter((id) => !setIds.has(id))
        .map((id) => ({ id, status: "binding" }));
      return [...prev, ...add];
    });

    setHardwareIds("");
    handleDeviceBinding(ids);
  };

  const handleDiscard = () => {
    setHardwareIds("");
    onClose?.();
  };

  const busy = binding || unbinding;

  const boundCount = devices.filter((d) => d.status === true).length;

  /**
   * A bound machine still does nothing until it lists an operation, so the guidance
   * appears once at least one bind lands. It sits under the summary rather than
   * replacing it — the per-device result is the receipt and has to stay readable.
   */
  const bindOutcome =
    boundCount > 0 && !busy
      ? {
          heading: `${boundCount} machine${boundCount === 1 ? "" : "s"} bound`,
          subheading: selectedOperations.length
            ? "Each one lists the operations you picked above."
            : "None of them list an operation yet, so no route step can pick them.",
          steps: [
            ...(selectedOperations.length
              ? []
              : [
                  {
                    label: "Give a machine its capability",
                    why: "A machine is only eligible for operations it lists.",
                    path: ["Manage", "Devices", "open the machine", "Capability"],
                    to: "/devices",
                  },
                ]),
            {
              label: "Use it in a part's route",
              why: "A route step only offers machines that list its operation.",
              path: ["Setup", "Parts", "open a part", "Route"],
              to: "/parts",
            },
            {
              label: "Schedule a run on it",
              why: "The run assigns one machine per step and tracks it from there.",
              path: ["Plan", "Job Schedules", "Add"],
              to: "/job-schedules",
            },
          ],
        }
      : null;

  const handleStepNavigate = (to) => {
    onClose?.();
    navigate(to);
  };

  return (
    <Offcanvas
      show={isOpen}
      onHide={onClose}
      placement="end"
      backdrop={true}
      style={{
        "--bs-offcanvas-width": "unset",
        width: "480px",
        "--bs-offcanvas-height": "unset",
        height: "100%",
      }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <h2 className="tw-text-lg tw-font-semibold">Bind Device</h2>
          <div className="tw-text-sm tw-text-gray-500">
            You can add multiple IDs with a comma separated.
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="tw-h-full tw-bg-white tw-p-2 tw-flex tw-flex-col">
          <label
            className="tw-text-sm tw-font-medium tw-mt-4 tw-mb-2"
            htmlFor="hardwareIds"
          >
            Hardware ID(s) <span className="tw-text-red-500">*</span>
          </label>

          <textarea
            id="hardwareIds"
            className="tw-w-full tw-p-2 tw-border tw-rounded-md tw-h-32 tw-resize-none"
            placeholder="Enter comma separated hardware id's"
            value={hardwareIds}
            onChange={(e) => setHardwareIds(e.target.value)}
            disabled={busy}
          />

          <label className="tw-text-sm tw-font-medium tw-mt-4 tw-mb-2">
            Operations
          </label>
          <Select
            options={operationOptions}
            placeholder={
              loadingOperations
                ? "Loading operations..."
                : "Select operations (optional)"
            }
            isMulti
            isDisabled={busy || loadingOperations}
            value={selectedOperations}
            onChange={(val) => setSelectedOperations(val || [])}
            closeMenuOnSelect={true}
            hideSelectedOptions={true}
          />
          <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
            Applied to every device in this batch. Leave empty to bind without
            operations — you can set them later from the device settings panel.
          </div>

          {devices.length > 0 && (
            <div className="tw-mt-6">
              <h3 className="tw-text-sm tw-font-medium tw-mb-2">Summary</h3>
              <div className="tw-space-y-3">
                {devices.map((device, index) => (
                  <div
                    key={`${device.id}-${index}`}
                    className="tw-bg-gray-50 tw-p-3 tw-rounded-md"
                  >
                    <div className="tw-flex tw-justify-between tw-items-center">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <i
                          className="fa fa-microchip tw-text-gray-600 tw-text-lg"
                          aria-hidden="true"
                        />
                        <span className="tw-font-medium">{device.id}</span>
                      </div>

                      {device.status === "binding" && (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-blue-500">
                          <span>Binding in Progress...</span>
                          <Loader2 className="tw-animate-spin tw-h-4 tw-w-4" />
                        </div>
                      )}

                      {device.status === true && device.created_at && (
                        <span className="tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-1 tw-rounded-md tw-text-sm">
                          Success
                        </span>
                      )}

                      {device.status === false && (
                        <span className="tw-bg-red-100 tw-text-red-800 tw-px-2 tw-py-1 tw-rounded-md tw-text-sm">
                          Failed
                        </span>
                      )}
                    </div>

                    {device.status === false && device.errorMessage && (
                      <div className="tw-mt-2 tw-flex tw-justify-between">
                        <p className="tw-mt-1 tw-text-xs tw-text-gray-600 tw-w-[60%]">
                          {device.errorMessage}
                        </p>
                        <button
                          className="tw-mt-2 tw-text-blue-600 tw-border tw-font-medium tw-border-blue-600 tw-rounded-md tw-px-2 tw-text-sm tw-w-[30%]"
                          onClick={() => handleDeviceUnbinding(device.id)}
                          disabled={busy}
                        >
                          Request Unbind
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {bindOutcome && (
            <div className="tw-mt-6 tw-border-t tw-pt-4">
              <SaveSuccessPanel
                heading={bindOutcome.heading}
                subheading={bindOutcome.subheading}
                steps={bindOutcome.steps}
                onNavigate={handleStepNavigate}
              />
            </div>
          )}

          <div className="tw-flex tw-justify-end tw-mt-auto tw-gap-2">
            <button
              className="tw-px-4 tw-py-2 tw-text-gray-600 tw-bg-gray-200 tw-rounded-md"
              onClick={handleDiscard}
              disabled={busy}
            >
              Discard
            </button>
            <button
              className={`tw-px-4 tw-py-2 tw-text-white tw-rounded-md ${
                hardwareIds
                  ? "tw-bg-blue-600 tw-hover:bg-blue-700"
                  : "tw-bg-gray-400 tw-cursor-not-allowed"
              }`}
              onClick={handleStartBinding}
              disabled={!hardwareIds || busy}
            >
              {binding ? "Starting..." : "Start Binding"}
            </button>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BindDeviceSidebar;
